import React, { useEffect, useState } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { logout } from './Api/auth';
import DashboardLayout from './layouts/dashboard';
import SimpleLayout from './layouts/simple';

import LoginPage from './pages/LoginPage';
import NotAuthorized from './pages/NotAuthorized';
import Page404 from './pages/Page404';
import ProductsPage from './pages/ProductsPage';
import DashboardAppPage from './pages/DashboardAppPage';
import Shedular from './pages/Shedular';
import Notifications from './pages/Notifications';
import ManageTest from './pages/ManageTest';
import ManageUAC from './pages/ManageUAC';
import ManageNotification from './pages/ManageNotification';
import CreateUAC from './pages/CreateUAC';
import ManageUser from './pages/ManageUser'; // Import ManageUser
import UserPage from './pages/UserPage';
import Courses from './pages/Courses';
import CreateCourse from './pages/CreateCourse';
import TeachersEdgeUAC from './pages/TeachersEdgeUAC';
import StudentUAC from './pages/StudentUAC';
import CreateStudentUAC from './pages/CreateStudentUAC';
import CreateTeacharsUAC from './pages/CreateTeacharsUAC';
import AssignRole from './pages/AssignRole';
import ClientRegister from './pages/ClientRegister';
import CreateClient from './pages/CreateClient';
import QuizDashboard from './pages/QuizDashboard';
import Activity from './pages/Activity';
import GetQuizByTags from './pages/GetQuizByTags';
import PlayQuizPage from './pages/PlayQuizPage';
import ImportQuiz from './pages/ImportQuiz';
import QuizReports from './pages/QuizReports';
import ExpiredQuiz from './pages/ExpiredQuiz';
import ManageDemoUAC from './pages/ManageDemoUAC';
import CreateDemoUAC from './pages/CreateDemoUAC';
import StudentList from './pages/StudentList';
import StudentReport from './pages/StudentReport';
import AssignmentResult from './pages/AssignmentResult';
import StudentLectureReport from './pages/StudentLectureReport';
import InvoiceList from './pages/InvoiceList';
import ShiprocketManifest from './pages/ShiprocketManifest';
import GenerateQRContent from './pages/GenerateQRContent';
import CreateUacForClient from './pages/CreateUacForClient';
import CreateShipmentPage from './pages/CreateShipment';
import GenerateAwbShipment from './pages/GenerateAwbShipment';
import EduteckInvoiceList from './pages/EduteckInvoiceList';
import CoursePage from './pages/CoursePage';
import SelectCourseAndSubject from './pages/SelectCourseAndSubject';
import ProjectListPage from './pages/ProjectList';
import CreateProject from './pages/CreateProject';
import CreateProjectChapter from './pages/CreateProjectChapter';
import EditProjectChapter from './pages/UpdateChapterDetails';
import ProjectChapterPage from './pages/ChapterList';
import ChapterHistoryPage from './pages/ChapterHistoy';

// True when this user's only page access is Invoice List — either an explicit
// access list containing just INVOICE_LIST, or (fallback, no access sent yet) the
// TEAM_KARAN role. Drives the same "confined to Invoice List" behavior for any
// future invoice-only role without needing another guard.
function isInvoiceOnlyUser() {
  const role = localStorage.getItem('role');
  const roles = role?.split(',') ?? [];
  let access;
  try {
    access = JSON.parse(localStorage.getItem('access'));
  } catch {
    access = [];
  }
  if (Array.isArray(access) && access.length > 0) {
    return access.every((key) => key === 'INVOICE_LIST');
  }
  return roles.includes('TEAM_KARAN');
}

// Blocks the whole dashboard when the backend has explicitly returned zero
// access (an empty array — not null/absent, which means "not implemented
// yet" and is left to the role-based fallback in nav/config.js).
function NotAuthorizedGuard({ children }) {
  let access;
  try {
    access = JSON.parse(localStorage.getItem('access'));
  } catch {
    access = null;
  }
  if (Array.isArray(access) && access.length === 0) {
    return <Navigate to="/not-authorized" />;
  }
  return children;
}

function InvoiceListGuard({ children }) {
  const role = localStorage.getItem('role');
  if (!role) {
    logout();
    window.location.replace('/login');
    return null;
  }
  return children;
}

function TeamKaranGuard({ children }) {
  if (isInvoiceOnlyUser()) return <Navigate to="/dashboard/InvoiceList" />;
  return children;
}

export default function Router() {
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (role) {
      setAuthenticated(true);
    }
  }, []);

  // Define the routes based on user role
  const getDashboardRoute = () => {
    let access;
    try {
      access = JSON.parse(localStorage.getItem('access'));
    } catch {
      access = null;
    }
    if (Array.isArray(access) && access.length === 0) {
      return '/not-authorized';
    }
    const role = localStorage.getItem('role');
    if (role === 'QUIZ_ADMIN') {
      return '/dashboard/QuizDashboard';
    }
    if (isInvoiceOnlyUser()) {
      return '/dashboard/InvoiceList';
    }
    return '/dashboard/app';
  };

  const routes = useRoutes([
    {
      path: '/',
      element: <Navigate to="/login" />, // Redirect to the login page by default
    },
    {
      path: '/login',
      element: authenticated ? <Navigate to={getDashboardRoute()} /> : <LoginPage />,
      index: true, // Set this as the default page
    },
    {
      path: '/not-authorized',
      element: <NotAuthorized />,
    },
    {
      path: '/dashboard',
      element: <NotAuthorizedGuard><DashboardLayout /></NotAuthorizedGuard>,
      children: [
        { element: <Navigate to={getDashboardRoute()} />, index: true },
        { path: 'app', element: <TeamKaranGuard><DashboardAppPage /></TeamKaranGuard> },
        { path: 'user', element: <TeamKaranGuard><UserPage /></TeamKaranGuard> },
        { path: 'manageuser', element: <TeamKaranGuard><ManageUser /></TeamKaranGuard> },
        { path: 'products', element: <TeamKaranGuard><ProductsPage /></TeamKaranGuard> },
        { path: 'Shedular', element: <TeamKaranGuard><Shedular /></TeamKaranGuard> },
        { path: 'Notification', element: <TeamKaranGuard><Notifications /></TeamKaranGuard> },
        { path: 'ManageTest', element: <TeamKaranGuard><ManageTest /></TeamKaranGuard> },
        { path: 'ManageUAC', element: <TeamKaranGuard><ManageUAC /></TeamKaranGuard> },
        { path: 'ManageNotification', element: <TeamKaranGuard><ManageNotification /></TeamKaranGuard> },
        { path: 'CreateUAC', element: <TeamKaranGuard><CreateUAC /></TeamKaranGuard> },
        { path: 'Courses', element: <TeamKaranGuard><Courses /></TeamKaranGuard> },
        { path: 'CreateCourse', element: <TeamKaranGuard><CreateCourse /></TeamKaranGuard> },
        // { path: 'CourseStructure', element: <CourseStructure /> },
        { path: 'StudentUAC', element: <TeamKaranGuard><StudentUAC /></TeamKaranGuard> },
        { path: 'TeachersEdgeUAC', element: <TeamKaranGuard><TeachersEdgeUAC /></TeamKaranGuard> },
        { path: 'CreateStudentUAC', element: <TeamKaranGuard><CreateStudentUAC /></TeamKaranGuard> },
        { path: 'CreateUacForClient', element: <TeamKaranGuard><CreateUacForClient /></TeamKaranGuard> },
        { path: 'CreateTeacharsUAC', element: <TeamKaranGuard><CreateTeacharsUAC /></TeamKaranGuard> },
        { path: 'AssignRole', element: <TeamKaranGuard><AssignRole /></TeamKaranGuard> },
        { path: 'ClientRegister', element: <TeamKaranGuard><ClientRegister /></TeamKaranGuard> },
        { path: 'CreateClient', element: <TeamKaranGuard><CreateClient /></TeamKaranGuard> },
        { path: 'QuizDashboard', element: <TeamKaranGuard><QuizDashboard /></TeamKaranGuard> },
        { path: 'Activity', element: <TeamKaranGuard><Activity /></TeamKaranGuard> },
        { path: 'GetQuizByTags', element: <TeamKaranGuard><GetQuizByTags /></TeamKaranGuard> },
        { path: 'PlayQuizPage', element: <TeamKaranGuard><PlayQuizPage /></TeamKaranGuard> },
        { path: 'ImportQuiz', element: <TeamKaranGuard><ImportQuiz /></TeamKaranGuard> },
        { path: 'QuizReports', element: <TeamKaranGuard><QuizReports /></TeamKaranGuard> },
        { path: 'ExpiredQuiz', element: <TeamKaranGuard><ExpiredQuiz /></TeamKaranGuard> },
        { path: 'ManageDemoUAC', element: <TeamKaranGuard><ManageDemoUAC /></TeamKaranGuard> },
        { path: 'CreateDemoUAC', element: <TeamKaranGuard><CreateDemoUAC /></TeamKaranGuard> },
        { path: 'StudentList', element: <TeamKaranGuard><StudentList /></TeamKaranGuard> },
        { path: 'StudentReport', element: <TeamKaranGuard><StudentReport /></TeamKaranGuard> },
        { path: 'AssignmentResult', element: <TeamKaranGuard><AssignmentResult /></TeamKaranGuard> },
        { path: 'StudentLectureReport', element: <TeamKaranGuard><StudentLectureReport /></TeamKaranGuard> },
        { path: 'InvoiceList', element: <InvoiceListGuard><InvoiceList /></InvoiceListGuard> },
        { path: 'ShiprocketManifest', element: <TeamKaranGuard><ShiprocketManifest /></TeamKaranGuard> },
        { path: 'CreateShipment', element: <TeamKaranGuard><CreateShipmentPage /></TeamKaranGuard> },
        { path: 'GenerateAWB', element: <TeamKaranGuard><GenerateAwbShipment /></TeamKaranGuard> },
        { path: 'GenerateQRContent', element: <TeamKaranGuard><GenerateQRContent /></TeamKaranGuard> },
        { path: 'EduteckInvoiceList', element: <TeamKaranGuard><EduteckInvoiceList /></TeamKaranGuard> },
        { path: 'CourseStucture', element: <TeamKaranGuard><CoursePage /></TeamKaranGuard> },
        { path: 'SelectCourseAndSubject', element: <TeamKaranGuard><SelectCourseAndSubject /></TeamKaranGuard> },
        { path: 'manageproject', element: <TeamKaranGuard><CreateProject /></TeamKaranGuard> },
        { path: 'projectlist', element: <TeamKaranGuard><ProjectListPage /></TeamKaranGuard> },
        { path: 'CreateChapter', element: <TeamKaranGuard><CreateProjectChapter /></TeamKaranGuard> },
        { path: 'UpdateChapterDetails', element: <TeamKaranGuard><EditProjectChapter /></TeamKaranGuard> },
        { path: 'ProjectChapters', element: <TeamKaranGuard><ProjectChapterPage /></TeamKaranGuard> },
        { path: 'ChapterHistory', element: <TeamKaranGuard><ChapterHistoryPage /></TeamKaranGuard> },
      ],
    },
    {
      path: '/404',
      element: <SimpleLayout />,
      children: [{ element: <Page404 /> }],
    },
    {
      path: '*',
      element: <Navigate to="/404" />,
    },
  ]);

  return routes;
}
